/**
 * PackageProgressIndicator - UI component for showing package download progress
 */

import { $dom } from '../utils/DOM';
import { getProgress, setProgressCallback, abortDownload, PackageProgress } from './Package';

let progressElement: HTMLElement | null = null;
let tooltipElement: HTMLElement | null = null;
let successTimeout: number | null = null;
let errorTimeout: number | null = null;

// SVG element references for stable DOM
let svgElement: SVGSVGElement | null = null;
let progressCircle: SVGCircleElement | null = null;
let successCircle: SVGCircleElement | null = null;
let successPath: SVGPathElement | null = null;
let errorCircle: SVGCircleElement | null = null;
let errorPath1: SVGLineElement | null = null;
let errorPath2: SVGLineElement | null = null;

// Tooltip element references
let tooltipProgressBar: HTMLElement | null = null;
let tooltipPercentText: HTMLElement | null = null;
let tooltipRemainingText: HTMLElement | null = null;

function formatBytes(bytes: number): string {
	if (bytes === 0) return '0 B';
	const k = 1024;
	const sizes = ['B', 'KB', 'MB', 'GB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

function createProgressElement(): HTMLElement {
	const element = document.createElement('div');
	element.id = 'package-progress-indicator';
	element.className = 'package-progress-indicator';
	element.style.cssText = `
		position: relative;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 20px;
		height: 20px;
		margin-left: auto;
		cursor: pointer;
	`;

	// Create SVG structure once and keep it stable
	svgElement = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
	svgElement.setAttribute('width', '20');
	svgElement.setAttribute('height', '20');
	svgElement.setAttribute('viewBox', '0 0 20 20');

	// Background circle (always visible)
	const bgCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
	bgCircle.setAttribute('cx', '10');
	bgCircle.setAttribute('cy', '10');
	bgCircle.setAttribute('r', '8');
	bgCircle.setAttribute('fill', 'none');
	bgCircle.setAttribute('stroke', '#e0e0e0');
	bgCircle.setAttribute('stroke-width', '2');
	svgElement.appendChild(bgCircle);

	// Progress circle (for downloading state)
	progressCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
	progressCircle.setAttribute('cx', '10');
	progressCircle.setAttribute('cy', '10');
	progressCircle.setAttribute('r', '8');
	progressCircle.setAttribute('fill', 'none');
	progressCircle.setAttribute('stroke', '#4a90e2');
	progressCircle.setAttribute('stroke-width', '2');
	progressCircle.setAttribute('stroke-dasharray', String(2 * Math.PI * 8));
	progressCircle.setAttribute('transform', 'rotate(-90 10 10)');
	progressCircle.setAttribute('stroke-linecap', 'round');
	svgElement.appendChild(progressCircle);

	// Success circle
	successCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
	successCircle.setAttribute('cx', '10');
	successCircle.setAttribute('cy', '10');
	successCircle.setAttribute('r', '8');
	successCircle.setAttribute('fill', '#4caf50');
	successCircle.style.display = 'none';
	svgElement.appendChild(successCircle);

	// Success checkmark
	successPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
	successPath.setAttribute('d', 'M6 10 L9 13 L14 7');
	successPath.setAttribute('stroke', 'white');
	successPath.setAttribute('stroke-width', '2');
	successPath.setAttribute('fill', 'none');
	successPath.setAttribute('stroke-linecap', 'round');
	successPath.setAttribute('stroke-linejoin', 'round');
	successPath.style.display = 'none';
	svgElement.appendChild(successPath);

	// Error circle
	errorCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
	errorCircle.setAttribute('cx', '10');
	errorCircle.setAttribute('cy', '10');
	errorCircle.setAttribute('r', '8');
	errorCircle.setAttribute('fill', '#f44336');
	errorCircle.style.display = 'none';
	svgElement.appendChild(errorCircle);

	// Error X lines
	errorPath1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
	errorPath1.setAttribute('x1', '7');
	errorPath1.setAttribute('y1', '7');
	errorPath1.setAttribute('x2', '13');
	errorPath1.setAttribute('y2', '13');
	errorPath1.setAttribute('stroke', 'white');
	errorPath1.setAttribute('stroke-width', '2');
	errorPath1.setAttribute('stroke-linecap', 'round');
	errorPath1.style.display = 'none';
	svgElement.appendChild(errorPath1);

	errorPath2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
	errorPath2.setAttribute('x1', '13');
	errorPath2.setAttribute('y1', '7');
	errorPath2.setAttribute('x2', '7');
	errorPath2.setAttribute('y2', '13');
	errorPath2.setAttribute('stroke', 'white');
	errorPath2.setAttribute('stroke-width', '2');
	errorPath2.setAttribute('stroke-linecap', 'round');
	errorPath2.style.display = 'none';
	svgElement.appendChild(errorPath2);

	element.appendChild(svgElement);
	return element;
}

function createTooltipElement(): HTMLElement {
	const tooltip = document.createElement('div');
	tooltip.className = 'package-progress-tooltip';
	tooltip.style.cssText = `
		position: absolute;
		bottom: 100%;
		right: 0;
		margin-bottom: 8px;
		background: white;
		border: 1px solid #ccc;
		border-radius: 4px;
		padding: 12px;
		min-width: 250px;
		box-shadow: 0 2px 8px rgba(0,0,0,0.15);
		z-index: 1000;
		display: none;
		pointer-events: none;
	`;

	// Create stable tooltip structure
	const title = document.createElement('div');
	title.style.cssText = 'margin-bottom: 8px; font-weight: bold;';
	title.textContent = 'Downloading Package';
	tooltip.appendChild(title);

	const progressBarContainer = document.createElement('div');
	progressBarContainer.style.cssText = 'margin-bottom: 8px;';
	const progressBarBg = document.createElement('div');
	progressBarBg.style.cssText = 'width: 100%; height: 8px; background: #e0e0e0; border-radius: 4px; overflow: hidden;';
	tooltipProgressBar = document.createElement('div');
	tooltipProgressBar.style.cssText = 'width: 0%; height: 100%; background: #4a90e2; transition: width 0.2s;';
	progressBarBg.appendChild(tooltipProgressBar);
	progressBarContainer.appendChild(progressBarBg);
	tooltip.appendChild(progressBarContainer);

	tooltipPercentText = document.createElement('div');
	tooltipPercentText.style.cssText = 'font-size: 12px; color: #666; margin-bottom: 4px;';
	tooltip.appendChild(tooltipPercentText);

	tooltipRemainingText = document.createElement('div');
	tooltipRemainingText.style.cssText = 'font-size: 12px; color: #666; margin-bottom: 8px;';
	tooltip.appendChild(tooltipRemainingText);

	const clickHint = document.createElement('div');
	clickHint.style.cssText = 'font-size: 11px; color: #999; font-style: italic;';
	clickHint.textContent = 'Click to cancel download';
	tooltip.appendChild(clickHint);

	return tooltip;
}

function updateProgressIndicator(progress: PackageProgress): void {
	if (!progressElement || !svgElement) return;

	// Clear any existing timeouts
	if (successTimeout !== null) {
		clearTimeout(successTimeout);
		successTimeout = null;
	}
	if (errorTimeout !== null) {
		clearTimeout(errorTimeout);
		errorTimeout = null;
	}

	const state = progress.state;

	if (state === 'idle') {
		progressElement.style.display = 'none';
		return;
	}

	progressElement.style.display = 'inline-flex';

	if (state === 'downloading') {
		// Show progress circle, hide success/error elements
		if (progressCircle) progressCircle.style.display = '';
		if (successCircle) successCircle.style.display = 'none';
		if (successPath) successPath.style.display = 'none';
		if (errorCircle) errorCircle.style.display = 'none';
		if (errorPath1) errorPath1.style.display = 'none';
		if (errorPath2) errorPath2.style.display = 'none';

		// Update progress circle dashoffset
		const percent = progress.total ? (progress.downloaded / progress.total) * 100 : 0;
		if (progressCircle) {
			const circumference = 2 * Math.PI * 8;
			progressCircle.setAttribute('stroke-dashoffset', String(circumference * (1 - percent / 100)));
		}
	} else if (state === 'success') {
		// Show success elements, hide others
		if (progressCircle) progressCircle.style.display = 'none';
		if (successCircle) successCircle.style.display = '';
		if (successPath) successPath.style.display = '';
		if (errorCircle) errorCircle.style.display = 'none';
		if (errorPath1) errorPath1.style.display = 'none';
		if (errorPath2) errorPath2.style.display = 'none';

		successTimeout = window.setTimeout(() => {
			if (progressElement) {
				progressElement.style.display = 'none';
			}
		}, 2000);
	} else if (state === 'error' || state === 'aborted') {
		// Show error elements, hide others
		if (progressCircle) progressCircle.style.display = 'none';
		if (successCircle) successCircle.style.display = 'none';
		if (successPath) successPath.style.display = 'none';
		if (errorCircle) errorCircle.style.display = '';
		if (errorPath1) errorPath1.style.display = '';
		if (errorPath2) errorPath2.style.display = '';

		errorTimeout = window.setTimeout(() => {
			if (progressElement) {
				progressElement.style.display = 'none';
			}
		}, 2000);
	}

	updateTooltip(progress);
}

function updateTooltip(progress: PackageProgress): void {
	if (!tooltipElement) return;

	const state = progress.state;

	if (state === 'idle' || state === 'success' || state === 'error' || state === 'aborted') {
		tooltipElement.style.display = 'none';
		return;
	}

	// Calculate percentage based on file count
	const percent = progress.total ? (progress.downloaded / progress.total) * 100 : 0;
	
	// Show file count with percentage
	const fileCountText = `${progress.downloaded} / ${progress.total || '?'} files (${percent.toFixed(1)}%)`;

	if (tooltipProgressBar) {
		tooltipProgressBar.style.width = `${percent}%`;
	}
	if (tooltipPercentText) {
		tooltipPercentText.textContent = fileCountText;
	}
	if (tooltipRemainingText) {
		tooltipRemainingText.textContent = '';
	}
}

function setupEventHandlers(): void {
	if (!progressElement || !tooltipElement) return;

	progressElement.addEventListener('mouseenter', () => {
		if (tooltipElement && progressElement) {
			const progress = getProgress();
			if (progress.state === 'downloading') {
				tooltipElement.style.display = 'block';
			}
		}
	});

	progressElement.addEventListener('mouseleave', () => {
		if (tooltipElement) {
			tooltipElement.style.display = 'none';
		}
	});

	progressElement.addEventListener('click', () => {
		const progress = getProgress();
		if (progress.state === 'downloading') {
			abortDownload();
		}
	});
}

/**
 * Initialize the progress indicator in the status bar
 */
export function initializeProgressIndicator(): void {
	const statusBar = $dom('#statusbar').get(0);
	if (!statusBar) return;

	progressElement = createProgressElement();
	tooltipElement = createTooltipElement();
	progressElement.appendChild(tooltipElement);
	statusBar.appendChild(progressElement);

	setupEventHandlers();

	setProgressCallback((progress) => {
		updateProgressIndicator(progress);
	});
}
